'use client';
import React from 'react';

// ─── Factor Detail Drilldown ─────────────────────────────────────────────────

// Shape returned by /api/factor-detail (only the fields this panel reads).
export type FactorDetailEntry = {
  formula?: string;
  description?: string | null;
  inputs?: Record<string, unknown>;
  result?: number | null;
  source?: string;
  period?: string;
  override?: string | null;
};
export type FactorDetailData = { factors?: Record<string, FactorDetailEntry>; _symbol?: string };

export function FactorDetailPanel({ factorKey, data, loading }: { factorKey: string; data: FactorDetailData | null; loading: boolean }) {
  if (loading) return <div className="text-[8px] text-[#64748b] py-1 pl-2">Loading...</div>;

  const detail = data?.factors?.[factorKey];
  if (!detail) return <div className="text-[8px] text-[#64748b] py-1 pl-2">No detail available</div>;

  const fmtNum = (v: number | null | undefined) => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
    if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
    if (Math.abs(v) < 0.01 && v !== 0) return v.toFixed(4);
    if (Math.abs(v) < 100) return v.toFixed(2);
    return v.toLocaleString();
  };

  const fmtPct = (v: number | null | undefined) => {
    if (v == null) return '—';
    return `${(v * 100).toFixed(1)}%`;
  };

  return (
    <div className="ml-2 mb-1 p-2 rounded text-[9px] leading-relaxed" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
      {detail.formula && (
        <div className="mb-1.5">
          <div className="text-[7px] text-[#64748b] uppercase tracking-widest mb-0.5">Formula</div>
          <div className="text-[9px] text-[#c9a84c] font-mono font-semibold">{detail.formula}</div>
        </div>
      )}
      {detail.description && (
        <div className="mb-1.5 text-[8px] text-[#94a3b8] leading-relaxed italic">{detail.description}</div>
      )}
      {detail.inputs && Object.keys(detail.inputs).length > 0 && (
        <div className="text-[7px] text-[#64748b] uppercase tracking-widest mb-0.5 mt-1">Raw Inputs</div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {detail.inputs && Object.entries(detail.inputs).map(([key, val]) => (
          <React.Fragment key={key}>
            <div className="text-[#94a3b8]">{key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, s => s.toUpperCase())
              .replace(/Eps/g, 'EPS')
              .replace(/Fcf/g, 'FCF')
              .replace(/Ev /g, 'EV ')
              .replace(/Ebitda/g, 'EBITDA')
              .replace(/Sga/g, 'SGA')
              .replace(/Roa/g, 'ROA')
              .replace(/Roe/g, 'ROE')
              .replace(/Roic/g, 'ROIC')
              .trim()}</div>
            <div className="text-[#e2e8f0] text-right font-mono tabular-nums">
              {typeof val === 'number'
                ? (key.toLowerCase().includes('margin') || key.toLowerCase().includes('ratio') || key.toLowerCase().includes('growth') || key.toLowerCase().includes('yield') || key.toLowerCase().includes('roe') || key.toLowerCase().includes('roa') || key.toLowerCase().includes('roic'))
                  ? fmtPct(val)
                  : fmtNum(val)
                : String(val ?? '—')}
            </div>
          </React.Fragment>
        ))}
      </div>
      {detail.result != null && (
        <div className="mt-1.5 pt-1 border-t border-[#1e293b] flex justify-between">
          <span className="text-[#64748b]">Result</span>
          <span className="text-white font-semibold font-mono">
            {typeof detail.result === 'number' && Math.abs(detail.result) < 10 ? fmtPct(detail.result) : fmtNum(detail.result)}
          </span>
        </div>
      )}
      <div className="mt-1 text-[7px] text-[#475569]">
        {detail.source && <span>Source: {detail.source}</span>}
        {detail.period && <span> · {detail.period}</span>}
        {detail.override && <span> · Bloomberg override applied</span>}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
